import { defineStore } from "pinia";
import { ref } from "vue";

/**
 * Toast-інфра (ADR-0010 D6). Стор + facade живуть у `shared/lib` (cross-cutting
 * UI-інфра без бізнес-логіки) — щоб пушити з будь-якого шару **вниз** і монтувати
 * раз глобально (`AppLayout`). Якби в `features` — `entities`/`shared` не пушили б
 * (upward-import). Презентаційні `Toast`/`ToastHost` — у `shared/ui`.
 */
export type ToastVariant = "default" | "success" | "destructive" | "info";

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title?: string;
  description?: string;
  /** ms до auto-dismiss; `0` — без авто-зникнення. */
  timeout: number;
}

export interface ToastInput {
  variant?: ToastVariant;
  title?: string;
  description?: string;
  timeout?: number;
}

/** Дефолтний час життя toast-а (UX-параметр, ADR-0010 Follow-up — уточнити з caller). */
const DEFAULT_TIMEOUT = 5000;
/** Cap черги — старіші витісняються (анти-флуд, spec §11.3). */
const MAX_ITEMS = 3;

let seq = 0;

export const useToastStore = defineStore("toast", () => {
  const items = ref<ToastItem[]>([]);
  // Таймери поза reactive-станом — імплементаційна деталь, не для UI.
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  function clearTimer(id: string): void {
    const t = timers.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      timers.delete(id);
    }
  }

  function dismiss(id: string): void {
    clearTimer(id);
    items.value = items.value.filter((it) => it.id !== id);
  }

  function push(input: ToastInput): string {
    const id = `toast-${++seq}`;
    const item: ToastItem = {
      id,
      variant: input.variant ?? "default",
      title: input.title,
      description: input.description,
      timeout: input.timeout ?? DEFAULT_TIMEOUT,
    };
    items.value.push(item);

    // Cap: витісняємо найстаріший, щоб найновіший лишився видимим.
    while (items.value.length > MAX_ITEMS) {
      const oldest = items.value[0];
      dismiss(oldest.id);
    }

    if (item.timeout > 0) {
      timers.set(
        id,
        setTimeout(() => dismiss(id), item.timeout),
      );
    }
    return id;
  }

  function clear(): void {
    for (const id of timers.keys()) {
      clearTimer(id);
    }
    items.value = [];
  }

  return { items, push, dismiss, clear };
});

/**
 * Імперативний facade для foreground-екшенів (ADR-0010 D5). `message` → `title`;
 * додаткові поля (`description`, `timeout`) — через `opts`. Викликається на
 * call-site під час роботи застосунку (активна Pinia гарантована).
 *
 * ---
 * **Toast standard for salonos admin SPA**
 *
 * MUTATION ACTIONS (create / update / delete):
 * - success          → `toast.success(message)`
 * - error            → `toast.error(message)`
 * - 404 / gone       → `toast.info(message)`    // entity already deleted
 * - recoverable info → `toast.info(message)`    // driven by error-map toastVariant
 *
 * READ / LOAD ACTIONS:
 * - success / error → silent (use status state: `listStatus` / `loadStatus` / `saveStatus`)
 *
 * Background mutations that fail visibly (e.g. optimistic reorder rollback) → `toast.error`.
 *
 * Rationale: reads are background ops; mutations are explicit user intent
 * that deserves feedback.
 */
export const toast = {
  success(message: string, opts?: Omit<ToastInput, "variant" | "title">): string {
    return useToastStore().push({ ...opts, variant: "success", title: message });
  },
  error(message: string, opts?: Omit<ToastInput, "variant" | "title">): string {
    return useToastStore().push({ ...opts, variant: "destructive", title: message });
  },
  info(message: string, opts?: Omit<ToastInput, "variant" | "title">): string {
    return useToastStore().push({ ...opts, variant: "info", title: message });
  },
};
